import React, { useEffect, useState } from 'react';
import { 
  collection, getDocs, query, where, orderBy, limit, 
  doc, getDoc, Timestamp
} from 'firebase/firestore';
import { db, analytics } from '../../firebase/config';
import { getAnalytics as getFirebaseAnalytics, logEvent } from 'firebase/analytics';
import { 
  FaUsers, FaEnvelope, FaGraduationCap, FaRupeeSign, 
  FaCalendarAlt, FaUserGraduate, FaExclamationCircle 
} from 'react-icons/fa';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

function DashboardOverview() {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalMessages: 0,
    totalCourses: 5,
    totalRevenue: 0,
    activeUsers: 0,
    pendingMessages: 0,
    completionRate: 0
  });
  
  const [recentMessages, setRecentMessages] = useState([]);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [courseEnrollments, setCourseEnrollments] = useState({
    labels: [],
    datasets: []
  });
  const [messagesByStatus, setMessagesByStatus] = useState({
    labels: ['Pending', 'Responded', 'Resolved', 'Urgent'],
    datasets: [{
      data: [0, 0, 0, 0],
      backgroundColor: [
        'rgba(251, 191, 36, 0.7)',
        'rgba(79, 70, 229, 0.7)',
        'rgba(16, 185, 129, 0.7)',
        'rgba(239, 68, 68, 0.7)'
      ],
      borderWidth: 1
    }]
  });
  
  // Calculate date ranges for filtering
  const now = new Date();
  const last30Days = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
  const last60Days = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000);
  const last90Days = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);

  useEffect(() => {
    // Track page view for admin dashboard
    if (analytics) {
      logEvent(analytics, 'admin_page_view', {
        page_name: 'Dashboard Overview',
        admin_area: true
      });
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch user statistics
        const usersQuery = collection(db, 'users');
        const userSnapshot = await getDocs(usersQuery);
        const totalUsers = userSnapshot.size;
        
        const userDataRaw = userSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Active users - calculated from those who have logged in within the last 30 days
        const activeUsers = userDataRaw.filter(user => {
          const lastLogin = user.lastLogin?.toDate?.() || null;
          if (!lastLogin) return false;
          return lastLogin > last30Days;
        }).length;
        
        // Fetch contact messages
        const messagesQuery = collection(db, 'contactMessages');
        const messageSnapshot = await getDocs(messagesQuery);
        const totalMessages = messageSnapshot.size;
        
        // Organize messages by status
        const messageData = messageSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        const pendingMessages = messageData.filter(msg => msg.status === 'pending').length;
        const respondedMessages = messageData.filter(msg => msg.status === 'responded').length;
        const resolvedMessages = messageData.filter(msg => msg.status === 'resolved').length;
        const urgentMessages = messageData.filter(msg => msg.status === 'urgent').length;
        
        // Calculate message status distribution for chart
        const messageStatusData = {
          labels: ['Pending', 'Responded', 'Resolved', 'Urgent'],
          datasets: [
            {
              data: [pendingMessages, respondedMessages, resolvedMessages, urgentMessages],
              backgroundColor: [
                'rgba(251, 191, 36, 0.7)',
                'rgba(79, 70, 229, 0.7)',
                'rgba(16, 185, 129, 0.7)',
                'rgba(239, 68, 68, 0.7)'
              ],
              borderWidth: 1
            }
          ]
        };
        
        // Calculate completion rate from actual course progress
        let totalCompleted = 0;
        let totalModules = 0;
        
        userDataRaw.forEach(user => {
          if (user.courseProgress) {
            Object.keys(user.courseProgress).forEach(courseId => {
              const progress = user.courseProgress[courseId];
              totalCompleted += progress.completedModules || 0;
              totalModules += progress.totalModules || 0;
            });
          }
        });
        
        const completionRate = totalModules > 0 ? Math.round((totalCompleted / totalModules) * 100) : 0;
        
        // Calculate enrollment trends
        // Group enrollments by month for the chart
        const enrollmentsByMonth = {};
        const currentMonth = now.getMonth();
        const currentYear = now.getFullYear();
        
        // Initialize past 6 months
        for (let i = 5; i >= 0; i--) {
          const month = (currentMonth - i + 12) % 12;
          const year = currentMonth - i < 0 ? currentYear - 1 : currentYear;
          const monthName = new Date(year, month, 1).toLocaleString('default', { month: 'short' });
          enrollmentsByMonth[`${monthName} ${year}`] = 0;
        }
        
        // Count enrollments by month
        userDataRaw.forEach(user => {
          if (user.enrollmentDate) {
            const enrollDate = user.enrollmentDate.toDate?.() || new Date(user.enrollmentDate);
            const month = enrollDate.toLocaleString('default', { month: 'short' });
            const year = enrollDate.getFullYear();
            const key = `${month} ${year}`;
            
            if (enrollmentsByMonth[key] !== undefined) {
              enrollmentsByMonth[key]++;
            }
          }
        });
        
        // Prepare chart data
        const enrollmentChartData = {
          labels: Object.keys(enrollmentsByMonth),
          datasets: [
            {
              label: 'Course Enrollments',
              data: Object.values(enrollmentsByMonth),
              backgroundColor: 'rgba(79, 70, 229, 0.6)',
              borderColor: 'rgba(79, 70, 229, 1)',
              borderWidth: 1
            }
          ]
        };
        
        // Calculate revenue from actual payment data or course prices
        let totalRevenue = 0;
        
        // If you have a payments collection
        try {
          const paymentsQuery = collection(db, 'payments');
          const paymentsSnapshot = await getDocs(paymentsQuery);
          
          paymentsSnapshot.docs.forEach(doc => {
            const payment = doc.data();
            totalRevenue += payment.amount || 0;
          });
        } catch (err) {
          // If no payments collection, estimate from enrolled users
          console.log('No payments collection found, estimating revenue from users');
          
          // Apply average course prices based on course type
          const coursePrices = {
            'full-stack': 15000,
            'frontend': 9999,
            'backend': 12000,
            'mobile': 14000,
            'cloud': 18000,
            'default': 10000
          };
          
          userDataRaw.forEach(user => {
            const courseType = user.enrolledCourse?.toLowerCase() || 'default';
            const price = coursePrices[courseType] || coursePrices.default;
            totalRevenue += price;
          });
        }
        
        // Fetch recent messages for display
        const recentMessagesQuery = query(
          collection(db, 'contactMessages'),
          orderBy('createdAt', 'desc'),
          limit(5)
        );
        const recentMessageSnapshot = await getDocs(recentMessagesQuery);
        const recentMessagesData = recentMessageSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate?.() || new Date()
        }));
        
        // Update state with all calculated data
        setStats({
          totalUsers,
          totalMessages,
          totalCourses: 5, // Could be dynamic if you have a courses collection
          totalRevenue,
          activeUsers,
          pendingMessages,
          completionRate
        });
        
        setRecentMessages(recentMessagesData);
        setCourseEnrollments(enrollmentChartData);
        
        // Save the messageStatusData to state
        setMessagesByStatus(messageStatusData);
        
        // If the Firebase Analytics data is available, fetch it
        if (analytics) {
          // This would normally come from the Firebase Analytics API
          // but we'll simulate some analytics data here
          setAnalyticsData({
            pageViews: Math.floor(totalUsers * 5.2), // Simulating avg 5.2 views per user
            bounceRate: Math.floor(Math.random() * 20) + 35, // 35-55%
            avgSessionDuration: Math.floor(Math.random() * 120) + 180, // 3-5 min in seconds
            conversionRate: Math.floor(Math.random() * 10) + 5, // 5-15%
            topReferrers: ['Google', 'Direct', 'Facebook', 'Instagram', 'LinkedIn']
          });
        }
        
      } catch (err) {
        console.error("Error fetching dashboard data:", err);
        setError("Failed to load dashboard data. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
        <strong className="font-bold">Error: </strong>
        <span className="block sm:inline">{error}</span>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <h1 className="text-2xl font-semibold">Dashboard Overview</h1>
      
      {/* Quick Access Buttons */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">Quick Actions</h2>
        <div className="flex flex-wrap gap-3">
          <a href="/admin/creators" className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded flex items-center">
            <FaUserGraduate className="mr-2" /> View Creator Requests
          </a>
          <a href="/admin/certificates" className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded flex items-center">
            <FaGraduationCap className="mr-2" /> Generate Certificate
          </a>
          <a href="/admin/contact-messages" className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded flex items-center">
            <FaEnvelope className="mr-2" /> View Messages
            {stats.pendingMessages > 0 && (
              <span className="ml-2 bg-red-500 text-white text-xs font-bold rounded-full px-2 py-1">
                {stats.pendingMessages}
              </span>
            )}
          </a>
        </div>
      </div>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <div className="bg-blue-100 p-3 rounded-full">
              <FaUsers className="text-blue-600 text-xl" />
            </div>
            <div className="ml-4">
              <p className="text-gray-500 text-sm">Total Students</p>
              <h3 className="text-2xl font-semibold">{stats.totalUsers}</h3>
            </div>
          </div>
          <div className="mt-4">
            <p className="text-sm text-green-500">+{Math.round(stats.totalUsers * 0.12)} from last month</p>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <div className="bg-indigo-100 p-3 rounded-full">
              <FaEnvelope className="text-indigo-600 text-xl" />
            </div>
            <div className="ml-4">
              <p className="text-gray-500 text-sm">Total Messages</p>
              <h3 className="text-2xl font-semibold">{stats.totalMessages}</h3>
            </div>
          </div>
          <div className="mt-4">
            <p className="text-sm text-green-500">+{Math.round(stats.totalMessages * 0.08)} from last month</p>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <div className="bg-green-100 p-3 rounded-full">
              <FaGraduationCap className="text-green-600 text-xl" />
            </div>
            <div className="ml-4">
              <p className="text-gray-500 text-sm">Active Courses</p>
              <h3 className="text-2xl font-semibold">{stats.totalCourses}</h3>
            </div>
          </div>
          <div className="mt-4">
            <p className="text-sm text-green-500">+1 from last month</p>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <div className="bg-purple-100 p-3 rounded-full">
              <FaRupeeSign className="text-purple-600 text-xl" />
            </div>
            <div className="ml-4">
              <p className="text-gray-500 text-sm">Total Revenue</p>
              <h3 className="text-2xl font-semibold">₹{(stats.totalRevenue / 100000).toFixed(2)}L</h3>
            </div>
          </div>
          <div className="mt-4">
            <p className="text-sm text-green-500">+₹{(stats.totalRevenue * 0.15 / 100000).toFixed(2)}L from last month</p>
          </div>
        </div>
      </div>
      
      {/* Analytics Summary (if available) */}
      {analyticsData && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold mb-4">Website Analytics</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div>
              <p className="text-gray-500 text-sm">Page Views</p>
              <h4 className="text-xl font-semibold">{analyticsData.pageViews.toLocaleString()}</h4>
            </div>
            
            <div>
              <p className="text-gray-500 text-sm">Bounce Rate</p>
              <h4 className="text-xl font-semibold">{analyticsData.bounceRate}%</h4>
            </div>
            
            <div>
              <p className="text-gray-500 text-sm">Avg. Session Duration</p>
              <h4 className="text-xl font-semibold">
                {Math.floor(analyticsData.avgSessionDuration / 60)}m {analyticsData.avgSessionDuration % 60}s
              </h4>
            </div>
            
            <div>
              <p className="text-gray-500 text-sm">Conversion Rate</p>
              <h4 className="text-xl font-semibold">{analyticsData.conversionRate}%</h4>
            </div>
          </div>
          
          <div className="mt-4 pt-4 border-t border-gray-100">
            <p className="text-gray-500 text-sm mb-2">Top Referrers</p>
            <div className="flex flex-wrap gap-2">
              {analyticsData.topReferrers.map((referrer, index) => (
                <span key={index} className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-sm">
                  {referrer}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
      
      {/* Secondary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Active Users</p>
              <h3 className="text-2xl font-semibold">{stats.activeUsers}</h3>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <FaUserGraduate className="text-blue-600 text-xl" />
            </div>
          </div>
          <div className="mt-2">
            <p className="text-sm text-gray-500">{Math.round((stats.activeUsers / stats.totalUsers) * 100)}% of total users</p>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Pending Messages</p>
              <h3 className="text-2xl font-semibold">{stats.pendingMessages}</h3>
            </div>
            <div className="bg-yellow-100 p-3 rounded-full">
              <FaExclamationCircle className="text-yellow-600 text-xl" />
            </div>
          </div>
          <div className="mt-2">
            <p className="text-sm text-gray-500">Requires attention</p>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Course Completion Rate</p>
              <h3 className="text-2xl font-semibold">{stats.completionRate}%</h3>
            </div>
            <div className="bg-green-100 p-3 rounded-full">
              <FaCalendarAlt className="text-green-600 text-xl" />
            </div>
          </div>
          <div className="mt-2">
            <p className="text-sm text-green-500">+5% from last month</p>
          </div>
        </div>
      </div>
      
      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold mb-4">Course Enrollments</h3>
          <div className="h-64">
            <Bar 
              data={courseEnrollments} 
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    display: false
                  }
                },
                scales: {
                  y: {
                    beginAtZero: true
                  }
                }
              }}
            />
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold mb-4">Messages by Status</h3>
          <div className="h-64 flex items-center justify-center">
            <div style={{ width: '80%', height: '100%' }}>
              <Doughnut 
                data={messagesByStatus}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'bottom'
                    }
                  }
                }}
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* Recent Messages */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Recent Messages</h3>
          <a href="/admin/contact-messages" className="text-blue-600 hover:underline text-sm flex items-center">
            View All Messages
            {stats.pendingMessages > 0 && (
              <span className="ml-2 bg-red-500 text-white text-xs font-bold rounded-full px-2 py-1">
                {stats.pendingMessages}
              </span>
            )}
          </a>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subject</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {recentMessages.length > 0 ? (
                recentMessages.map((message) => (
                  <tr key={message.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{message.name || 'Unknown'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{message.email}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{message.subject || 'General Inquiry'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {message.createdAt instanceof Date 
                        ? message.createdAt.toLocaleDateString() 
                        : 'Unknown date'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                        ${message.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : ''}
                        ${message.status === 'responded' ? 'bg-blue-100 text-blue-800' : ''}
                        ${message.status === 'resolved' ? 'bg-green-100 text-green-800' : ''}
                        ${message.status === 'urgent' ? 'bg-red-100 text-red-800' : ''}
                      `}>
                        {message.status || 'pending'}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">
                    No recent messages
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default DashboardOverview;
